/* eslint-disable prettier/prettier */
import React, { FC, useEffect, useRef } from 'react'

import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Fab,
  InputAdornment,
  Stack,
  SxProps,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
  useMediaQuery,
} from '@mui/material'

import {
  AccessTime,
  Add,
  DirectionsCar,
  EmojiFoodBeverage,
  FamilyRestroom,
  Healing,
  HourglassEmpty,
  LockOpen,
  Pets,
  RoomPreferences,
  Search as SearchIcon,
  SupervisedUserCircle,
  Tune,
  Verified,
} from '@mui/icons-material'
import {
  AccountDto,
  AccountDtoAvailabilityStatus,
  AccountDtoHomeHasIsolationRoom,
  AccountDtoPermission,
  AccountDtoPreferencesNeedsBottleFeeding,
  AccountDtoPreferencesSeniority,
  AccountDtoPreferencesWillingToSocializeWildAnimals,
  AccountDtoRoles,
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStatus,
  AnimalDtoSpecies,
  AnimalSearchItemDto,
} from '../../interactors/gen/backendClient'
import { CircularProgressPanel } from '../common/CircularProgressPanel'

import { useNavigate } from 'react-router-dom'
import {
  getColorForAvailabilityStatus,
  getReadableAccountFieldLabel,
  getReadableAccountPermission,
  getReadableAccountPreferencesSeniority,
  getReadableAccountRole,
  getReadableAvailabilityStatus,
  getSmallReadableAccountRole,
} from '../../domain/Account/AccountDisplay'
import { searchMembers } from '../../domain/Account/searchMembers'
import { getEmojiAnimalSex, getEmojiSpecies, getReadableSpecies } from '../../domain/Animal/AnimalDisplay'
import { BooleanOrAll } from '../../domain/enums/Boolean.enum'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import { useAccountsStore } from '../../store/AccountsStore'
import { useFetchOrRefreshMembersOnMount } from '../../store/useFetchOrRefreshMembersOnMount'
import { getAccountProfilePhotoSrc, getAnimalProfilePhotoSrc } from '../../utils/S3-links'
import { useComponentScrollRestoration } from '../../utils/hooks/useScrollRestoration'
import { isUserAvailableToday } from '../../utils/isUserAvailableToday'
import { setOpacity } from '../../utils/setOpacity'
import { sortAlphabetically } from '../../utils/sort'
import { PATHS } from '../PATHS'
import { FilterList } from '../common/FilterList'
import { FilterListItem } from '../common/FilterListItem'
import { OverviewFilterDialog, OverviewFilterSection } from '../common/OverviewFilterDialog'
import {
  overviewActionButtonSx,
  overviewFilterCountBadgeSx,
  overviewTabSx,
  overviewTabsSx,
} from '../common/overviewStyles'
import { searchFieldSx } from '../common/searchFieldSx'
import { addPrimaryFabSx } from '../common/addPrimaryCtaSx'
import { blueSky, colors, spacingItem, theme } from '../theme'
import MemberListCard from './MemberListCard'

export const ContactListScreen: FC = () => {
  const queryParams = new URLSearchParams(location.search)
  const getQueryParamOrDefault = <T extends string | null>(paramValue: string | null, defaultValue: T) => {
    if (paramValue == null || paramValue === '') {
      return defaultValue
    }
    return paramValue as T
  }
  const getArrayQueryParamOrDefault = (paramValue: string | null | undefined, defaultValue: string[]) => {
    const values = paramValue?.split('|')
    if (values && values.length === 1 && values[0] === '') {
      return defaultValue
    }
    return values ?? defaultValue
  }
  const [selectedStatus, setSelectedStatus] = React.useState<AccountDtoAvailabilityStatus | null>(
    getQueryParamOrDefault<AccountDtoAvailabilityStatus | null>(queryParams.get('selectedStatus'), null)
  )
  const [hasChildren, setHasChildren] = React.useState<BooleanOrAll>(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('hasChildren'), BooleanOrAll.All)
  )
  const [hasIsolationRoom, setHasIsolationRoom] = React.useState<BooleanOrAll>(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('hasIsolationRoom'), BooleanOrAll.All)
  )
  const [hasCar, setHasCar] = React.useState<BooleanOrAll>(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('hasCar'), BooleanOrAll.All)
  )
  const [agreeToBottleFeed, setAgreeToBottleFeed] = React.useState<BooleanOrAll>(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('agreeToBottleFeed'), BooleanOrAll.All)
  )
  const [agreeToHandleSickAnimals, setAgreeToHandleSickAnimals] = React.useState<BooleanOrAll>(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('agreeToHandleSickAnimals'), BooleanOrAll.All)
  )
  const [hasAnimalsOutsideAssociation, setHasAnimalsOutsideAssociation] = React.useState<BooleanOrAll>(
    getQueryParamOrDefault<BooleanOrAll>(queryParams.get('hasAnimalsOutsideAssociation'), BooleanOrAll.All)
  )
  const [selectedSpecies, setSelectedSpecies] = React.useState<AnimalDtoSpecies[]>(
    getArrayQueryParamOrDefault(queryParams.get('selectedSpecies'), []) as AnimalDtoSpecies[]
  )
  const [selectedSeniorities, setSelectedSeniorities] = React.useState<AccountDtoPreferencesSeniority[]>(
    getArrayQueryParamOrDefault(queryParams.get('selectedSeniorities'), []) as AccountDtoPreferencesSeniority[]
  )
  const [selectedPermissions, setSelectedPermissions] = React.useState<AccountDtoPermission[]>(
    getArrayQueryParamOrDefault(queryParams.get('selectedPermissions'), []) as AccountDtoPermission[]
  )
  const [selectedRole, setSelectedRole] = React.useState<AccountDtoRoles | null>(
    getQueryParamOrDefault<AccountDtoRoles | null>(queryParams.get('selectedRole'), null)
  )
  const [isArchived, setIsArchived] = React.useState<boolean>(queryParams.get('isArchived') === 'true')
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)
  const activeFiltersCount = [
    selectedStatus !== null,
    hasChildren !== BooleanOrAll.All,
    hasIsolationRoom !== BooleanOrAll.All,
    hasCar !== BooleanOrAll.All,
    agreeToBottleFeed !== BooleanOrAll.All,
    agreeToHandleSickAnimals !== BooleanOrAll.All,
    hasAnimalsOutsideAssociation !== BooleanOrAll.All,
    selectedSpecies.length > 0,
    selectedSeniorities.length > 0,
    selectedPermissions.length > 0,
  ].filter(Boolean).length

  const scrollRef = useRef<HTMLElement | null>(null)
  useComponentScrollRestoration(scrollRef, 'member-list')

  const handleTabChange = (_: unknown, newValue: AccountDtoRoles | 'archived') => {
    if (newValue === 'archived') {
      setSelectedRole(null)
      return setIsArchived(true)
    }
    setSelectedRole(newValue as AccountDtoRoles)
    setIsArchived(false)
  }

  const navigate = useNavigate()

  useFetchOrRefreshMembersOnMount()

  const accountsStore = useAccountsStore()

  const [searchQuery, setSearchQuery] = React.useState('')
  const resetFilters = () => {
    setSelectedStatus(null)
    setHasChildren(BooleanOrAll.All)
    setHasIsolationRoom(BooleanOrAll.All)
    setHasCar(BooleanOrAll.All)
    setAgreeToBottleFeed(BooleanOrAll.All)
    setAgreeToHandleSickAnimals(BooleanOrAll.All)
    setHasAnimalsOutsideAssociation(BooleanOrAll.All)
    setSelectedSpecies([])
    setSelectedSeniorities([])
    setSelectedPermissions([])
  }

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchQuery(e.target.value)
  }

  const species = accountsStore.connectedCustomer?.preferences.species

  useEffect(() => {
    const searchParams = new URLSearchParams()
    searchParams.set('search', searchQuery)
    searchParams.set('selectedStatus', selectedStatus ?? '')
    searchParams.set('hasChildren', hasChildren)
    searchParams.set('hasIsolationRoom', hasIsolationRoom)
    searchParams.set('hasCar', hasCar)
    searchParams.set('agreeToBottleFeed', agreeToBottleFeed)
    searchParams.set('agreeToHandleSickAnimals', agreeToHandleSickAnimals)
    searchParams.set('hasAnimalsOutsideAssociation', hasAnimalsOutsideAssociation)
    searchParams.set('selectedSpecies', selectedSpecies.join('|'))
    searchParams.set('selectedSeniorities', selectedSeniorities.join('|'))
    searchParams.set('selectedPermissions', selectedPermissions.join('|'))
    searchParams.set('selectedRole', selectedRole ?? '')
    searchParams.set('isArchived', isArchived.toString())
    // Add more parameters as needed
    navigate(`?${searchParams.toString()}`, { replace: true })
  }, [
    searchQuery,
    selectedStatus,
    hasChildren,
    hasIsolationRoom,
    hasCar,
    agreeToBottleFeed,
    agreeToHandleSickAnimals,
    hasAnimalsOutsideAssociation,
    selectedSpecies,
    selectedSeniorities,
    selectedPermissions,
    selectedRole,
    isArchived,
  ])

  const FiltersBar = () => {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <OverviewFilterSection>
          <Typography variant="h6" gutterBottom>
            Rechercher un membre
          </Typography>

          <FilterList label="Disponibilité" icon={<AccessTime />}>
            {Object.values(AccountDtoAvailabilityStatus).map((status) => {
              return (
                <FilterListItem
                  key={status}
                  label={getReadableAvailabilityStatus(status)}
                  isSelected={selectedStatus === status}
                  toggleSelected={() => {
                    if (selectedStatus === status) {
                      setSelectedStatus(null)
                    } else {
                      setSelectedStatus(status)
                    }
                  }}
                />
              )
            })}
          </FilterList>

          <FilterList label="Permission" icon={<LockOpen />}>
            {Object.values(AccountDtoPermission).map((permission) => {
              const isSelected = selectedPermissions.includes(permission)

              return (
                <FilterListItem
                  key={permission}
                  label={getReadableAccountPermission(permission)}
                  isSelected={isSelected}
                  toggleSelected={() => {
                    if (isSelected) {
                      // If the permission is already selected, remove it from the selectedPermissions array
                      setSelectedPermissions(selectedPermissions.filter((s) => s !== permission))
                    } else {
                      // If the permission is not selected, add it to the selectedSexs array
                      setSelectedPermissions([...selectedPermissions, permission])
                    }
                  }}
                />
              )
            })}
          </FilterList>

          <FilterList label="Rôle" icon={<SupervisedUserCircle />}>
            {Object.values(AccountDtoRoles).map((role) => {
              // Veterinaries have their own dedicated tab, so they are not filterable here.
              if (role === AccountDtoRoles.Veterinary) {
                return null
              }

              const isSelected = selectedRole === role

              return (
                <FilterListItem
                  key={role}
                  label={getReadableAccountRole(role)}
                  isSelected={isSelected}
                  toggleSelected={() => {
                    if (isSelected) {
                      // If the role is already selected, remove it from the selectedRoles array
                      setSelectedRole(null)
                    } else {
                      // If the role is not selected, add it to the selectedSexs array
                      setSelectedRole(role)
                    }
                  }}
                />
              )
            })}
          </FilterList>

          <Typography variant="h6" gutterBottom sx={spacingItem}>
            Préférences des membres
          </Typography>

          {species && species.length > 1 && (
            <FilterList label="Espèces" icon={<Pets />}>
              {species?.map((specie) => {
                const isSelected = selectedSpecies.includes(specie)

                return (
                  <FilterListItem
                    key={specie}
                    label={getReadableSpecies(specie)}
                    isSelected={isSelected}
                    toggleSelected={() => {
                      if (isSelected) {
                        // If the specie is already selected, remove it from the selectedSpecies array
                        setSelectedSpecies(selectedSpecies.filter((s) => s !== specie))
                      } else {
                        // If the specie is not selected, add it to the selectedSpecies array
                        setSelectedSpecies([...selectedSpecies, specie])
                      }
                    }}
                  />
                )
              })}
            </FilterList>
          )}

          <FilterList label="Enfants" icon={<FamilyRestroom />}>
            <FilterListItem
              label="Oui"
              isSelected={hasChildren === BooleanOrAll.True}
              toggleSelected={() => {
                if (hasChildren === BooleanOrAll.True) {
                  setHasChildren(BooleanOrAll.All)
                } else {
                  setHasChildren(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Non"
              isSelected={hasChildren === BooleanOrAll.False}
              toggleSelected={() => {
                if (hasChildren === BooleanOrAll.False) {
                  setHasChildren(BooleanOrAll.All)
                } else {
                  setHasChildren(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>

          <FilterList label="Isolement" icon={<RoomPreferences />}>
            <FilterListItem
              label="Oui"
              isSelected={hasIsolationRoom === BooleanOrAll.True}
              toggleSelected={() => {
                if (hasIsolationRoom === BooleanOrAll.True) {
                  setHasIsolationRoom(BooleanOrAll.All)
                } else {
                  setHasIsolationRoom(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Non"
              isSelected={hasIsolationRoom === BooleanOrAll.False}
              toggleSelected={() => {
                if (hasIsolationRoom === BooleanOrAll.False) {
                  setHasIsolationRoom(BooleanOrAll.All)
                } else {
                  setHasIsolationRoom(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>

          <FilterList label="Biberonnage" icon={<EmojiFoodBeverage />}>
            <FilterListItem
              label="Oui"
              isSelected={agreeToBottleFeed === BooleanOrAll.True}
              toggleSelected={() => {
                if (agreeToBottleFeed === BooleanOrAll.True) {
                  setAgreeToBottleFeed(BooleanOrAll.All)
                } else {
                  setAgreeToBottleFeed(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Non"
              isSelected={agreeToBottleFeed === BooleanOrAll.False}
              toggleSelected={() => {
                if (agreeToBottleFeed === BooleanOrAll.False) {
                  setAgreeToBottleFeed(BooleanOrAll.All)
                } else {
                  setAgreeToBottleFeed(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>

          <FilterList label="Véhiculé" icon={<DirectionsCar />}>
            <FilterListItem
              label="Oui"
              isSelected={hasCar === BooleanOrAll.True}
              toggleSelected={() => {
                if (hasCar === BooleanOrAll.True) {
                  setHasCar(BooleanOrAll.All)
                } else {
                  setHasCar(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Non"
              isSelected={hasCar === BooleanOrAll.False}
              toggleSelected={() => {
                if (hasCar === BooleanOrAll.False) {
                  setHasCar(BooleanOrAll.All)
                } else {
                  setHasCar(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>

          <FilterList label="Animaux hors asso" icon={<Pets />}>
            <FilterListItem
              label="Oui"
              isSelected={hasAnimalsOutsideAssociation === BooleanOrAll.True}
              toggleSelected={() => {
                if (hasAnimalsOutsideAssociation === BooleanOrAll.True) {
                  setHasAnimalsOutsideAssociation(BooleanOrAll.All)
                } else {
                  setHasAnimalsOutsideAssociation(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Non"
              isSelected={hasAnimalsOutsideAssociation === BooleanOrAll.False}
              toggleSelected={() => {
                if (hasAnimalsOutsideAssociation === BooleanOrAll.False) {
                  setHasAnimalsOutsideAssociation(BooleanOrAll.All)
                } else {
                  setHasAnimalsOutsideAssociation(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>

          <FilterList label="Accepte animaux malades" icon={<Healing />}>
            <FilterListItem
              label="Oui"
              isSelected={agreeToHandleSickAnimals === BooleanOrAll.True}
              toggleSelected={() => {
                if (agreeToHandleSickAnimals === BooleanOrAll.True) {
                  setAgreeToHandleSickAnimals(BooleanOrAll.All)
                } else {
                  setAgreeToHandleSickAnimals(BooleanOrAll.True)
                }
              }}
            />
            <FilterListItem
              label="Non"
              isSelected={agreeToHandleSickAnimals === BooleanOrAll.False}
              toggleSelected={() => {
                if (agreeToHandleSickAnimals === BooleanOrAll.False) {
                  setAgreeToHandleSickAnimals(BooleanOrAll.All)
                } else {
                  setAgreeToHandleSickAnimals(BooleanOrAll.False)
                }
              }}
            />
          </FilterList>

          <FilterList label="Âge recherché" icon={<HourglassEmpty />}>
            {Object.values(AccountDtoPreferencesSeniority).map((seniority) => {
              const isSelected = selectedSeniorities.includes(seniority)

              return (
                <FilterListItem
                  key={seniority}
                  label={getReadableAccountPreferencesSeniority(seniority)}
                  isSelected={isSelected}
                  toggleSelected={() => {
                    if (isSelected) {
                      // If the seniority is already selected, remove it from the selectedSenioritys array
                      setSelectedSeniorities(selectedSeniorities.filter((s) => s !== seniority))
                    } else {
                      // If the seniority is not selected, add it to the selectedSenioritys array
                      setSelectedSeniorities([...selectedSeniorities, seniority])
                    }
                  }}
                />
              )
            })}
          </FilterList>
        </OverviewFilterSection>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          minHeight: 0,
          padding: 0,
          backgroundColor: 'transparent',
          boxSizing: 'border-box',
          marginBottom: 0,
          maxHeight: '100%',
        }}
      >
        {accountsStore.isLoading ? <CircularProgressPanel /> : <></>}
        <Card
          sx={{
            ...spacingItem,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Tabs
            value={selectedRole ? selectedRole : isArchived ? 'archived' : null}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={overviewTabsSx}
          >
            <Tab label="Tous" value={null} sx={overviewTabSx} />
            <Tab label="Membres" value={AccountDtoRoles.Member} sx={overviewTabSx} />
            <Tab label="Familles d'accueil" value={AccountDtoRoles.HostFamily} sx={overviewTabSx} />
            <Tab label="Archivés" value={'archived'} sx={overviewTabSx} />
          </Tabs>

          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, mb: 1.5 }}
            >
              <TextField
                id="name-input"
                placeholder="Rechercher dans les membres"
                type="text"
                fullWidth
                value={searchQuery}
                onChange={onChangeSearch}
                sx={searchFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={
                  activeFiltersCount > 0 ? (
                    <Box component="span" sx={overviewFilterCountBadgeSx}>
                      {activeFiltersCount}
                    </Box>
                  ) : (
                    <Tune fontSize="small" />
                  )
                }
                onClick={() => setIsFiltersOpen(true)}
                sx={{ ...overviewActionButtonSx, flexShrink: 0, height: 40, py: 0 }}
              >
                Filtres
              </Button>
              {activeFiltersCount > 0 && (
                <Button
                  variant="text"
                  onClick={resetFilters}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    textTransform: 'none',
                    color: '#7A8197',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    px: 1,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: colors.cello,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Reinitialiser les filtres
                </Button>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              flexGrow: 1,
              minHeight: 0,
              boxSizing: 'border-box',
              overflow: 'auto',
              paddingBottom: 20, // HACK: To avoid the last row to be hidden
              transform: 'translateZ(0)',
            }}
            ref={scrollRef}
          >
            {accountsStore.isLoading ? (
              <CircularProgressPanel />
            ) : (
              <SearchResults
                members={accountsStore.members.filter(
                  (member) => !(member.roles.includes(AccountDtoRoles.Veterinary) && member.roles.length === 1)
                )}
                searchQuery={searchQuery}
                filters={{
                  selectedStatus,
                  hasChildren,
                  selectedSpecies,
                  selectedSeniorities,
                  selectedPermissions,
                  selectedRole,
                  hasIsolationRoom,
                  hasCar,
                  agreeToBottleFeed,
                  hasAnimalsOutsideAssociation,
                  agreeToHandleSickAnimals,
                  isArchived,
                }}
              />
            )}
          </Box>

          <OverviewFilterDialog open={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} title="Filtres membres">
            <FiltersBar />
          </OverviewFilterDialog>

          {accountsStore.isAdmin() && (
            <Box sx={{ position: 'absolute', right: 2, bottom: 0, margin: 2 }}>
              <Fab
                color="primary"
                variant="extended"
                onClick={() => navigate(PATHS.ajouterBenevole.absolute)}
                sx={{
                  ...addPrimaryFabSx,
                  px: 2.25,
                }}
              >
                <Add sx={{ mr: 1 }} />
                Ajouter un membre
              </Fab>
            </Box>
          )}
        </Card>
      </Box>
    </>
  )
}

type SortDirection = 'asc' | 'desc'
type SortColumn = 'firstName' | 'lastName' | 'status' | 'email' | 'address' | 'roles' | 'permission'

type SearchResultsProps = {
  members: Array<AccountDto>
  searchQuery: string
  filters?: {
    selectedStatus: AccountDtoAvailabilityStatus | null
    hasChildren: BooleanOrAll
    hasIsolationRoom: BooleanOrAll
    agreeToBottleFeed: BooleanOrAll
    hasCar: BooleanOrAll
    hasAnimalsOutsideAssociation: BooleanOrAll
    agreeToHandleSickAnimals: BooleanOrAll
    selectedSpecies: AnimalDtoSpecies[]
    selectedSeniorities: AccountDtoPreferencesSeniority[]
    selectedPermissions?: AccountDtoPermission[]
    selectedRole?: AccountDtoRoles | null
    isArchived: boolean
  }
}

const SearchResults: React.FC<SearchResultsProps> = ({ members, searchQuery, filters }) => {
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('status')
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc')
  const [expandCardIndex, setExpandCardIndex] = React.useState<number | null>(null)
  const accountsStore = useAccountsStore()
  const isMobile = useMediaQuery('(max-width:600px)')

  const handleSort = (column: SortColumn, columnDefaultDirection: SortDirection) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortDirection(columnDefaultDirection)
    }

    setSortColumn(column)
  }
  const searchResult = searchMembers(members, searchQuery)
    .map((member) => {
      // Calculate filtered preferences once and reuse them across filters
      const filteredPreferences =
        !filters?.selectedSpecies || filters?.selectedSpecies.length === 0
          ? [
              ...(member.preferences.primaryPreferences ? [member.preferences.primaryPreferences] : []),
              ...(member.preferences.otherPreferences && member.preferences.otherPreferences.length > 0
                ? member.preferences.otherPreferences
                : []),
            ]
          : [
              ...(filters?.selectedSpecies.includes(member.preferences.primaryPreferences?.specie as AnimalDtoSpecies)
                ? [member.preferences.primaryPreferences]
                : []),
              ...(member.preferences.otherPreferences?.filter((pref) =>
                filters?.selectedSpecies.includes(pref.specie as AnimalDtoSpecies)
              ) || []),
            ]

      // Compute seniorities from the filtered preferences
      const seniorities = [...(filteredPreferences?.map((pref) => pref?.seniority) || [])]
        .filter(Boolean)
        .flat() // Removes any undefined values and flattens the array
        .filter(Boolean) as AccountDtoPreferencesSeniority[]

      return { member, filteredPreferences, seniorities }
    })
    .filter(({ member }) => {
      if (!filters) return true
      switch (filters.hasChildren) {
        case BooleanOrAll.All:
          return true
        case BooleanOrAll.True:
          return member.home?.childrenNumber ? member.home?.childrenNumber > 0 : false
        case BooleanOrAll.False:
          return member.home?.childrenNumber ? member.home?.childrenNumber === 0 : true
      }
    })
    .filter(({ filteredPreferences }) => {
      // Filter based on selected species
      if (!filters || !filters.selectedSpecies || filters.selectedSpecies.length === 0) {
        return true
      }
      return filteredPreferences.length > 0
    })
    .filter(({ seniorities }) => {
      // Filter based on selected seniorities
      if (!filters || !filters.selectedSeniorities || filters.selectedSeniorities.length === 0) {
        return true
      }
      return seniorities.filter(Boolean).some((seniority) => filters.selectedSeniorities.includes(seniority))
    })
    .map(({ member }) => member)
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedPermissions?.length) {
        return true
      }
      return member.permission ? filters.selectedPermissions.includes(member.permission) : false
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedRole) {
        return true
      }
      return member.roles
        ? member.roles.some((roles) => roles.includes(filters.selectedRole as AccountDtoRoles))
        : false
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedStatus) {
        return true
      }
      return filters.selectedStatus === isUserAvailableToday(member)
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.hasIsolationRoom) return true
      if (filters.hasIsolationRoom === BooleanOrAll.All) return true

      return filters.hasIsolationRoom === 'true'
        ? member.home?.hasIsolationRoom === AccountDtoHomeHasIsolationRoom.Yes
        : member.home?.hasIsolationRoom === AccountDtoHomeHasIsolationRoom.No
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.hasCar) return true
      if (filters.hasCar === BooleanOrAll.All) return true

      return filters.hasCar === 'true'
        ? member.home?.hasCar === AccountDtoHomeHasIsolationRoom.Yes
        : member.home?.hasCar === AccountDtoHomeHasIsolationRoom.No
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.hasAnimalsOutsideAssociation) return true
      if (filters.hasAnimalsOutsideAssociation === BooleanOrAll.All) return true

      return filters.hasAnimalsOutsideAssociation === 'true'
        ? member.home?.pets && member.home.pets.length > 0
        : !member.home?.pets || member.home.pets.length === 0
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.agreeToBottleFeed) return true
      if (filters.agreeToBottleFeed === BooleanOrAll.All) return true

      return filters.agreeToBottleFeed === 'true'
        ? member.preferences?.primaryPreferences?.needsBottleFeeding === AccountDtoPreferencesNeedsBottleFeeding.True
        : member.preferences?.primaryPreferences?.needsBottleFeeding === AccountDtoPreferencesNeedsBottleFeeding.False
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.agreeToHandleSickAnimals) return true
      if (filters.agreeToHandleSickAnimals === BooleanOrAll.All) return true

      return filters.agreeToHandleSickAnimals === 'true'
        ? member.preferences?.primaryPreferences?.withMedicalAntecedent === AccountDtoHomeHasIsolationRoom.Yes
        : member.preferences?.primaryPreferences?.withMedicalAntecedent === AccountDtoHomeHasIsolationRoom.No
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.isArchived) return !member.visibility?.pauseProfile || member.visibility?.pauseProfile == null
      return filters.isArchived
        ? member.visibility?.pauseProfile === true
        : !member.visibility?.pauseProfile || member.visibility?.pauseProfile == null
    })
    .sort((a, b) => {
      switch (sortColumn) {
        case 'firstName':
          return sortAlphabetically(a, b, sortDirection, 'firstName')
        case 'lastName':
          return sortAlphabetically(a, b, sortDirection, 'lastName')
        case 'status':
          return sortAlphabetically(a, b, sortDirection, 'status')
        case 'email':
          return sortAlphabetically(a, b, sortDirection, 'email')
        case 'address':
          return sortAlphabetically(a, b, sortDirection, 'address')
        case 'roles':
          return sortAlphabetically(a, b, sortDirection, 'roles')
        case 'permission':
          return sortAlphabetically(a, b, sortDirection, 'permission')
        default:
          throw new Error(`sortColumn ${sortColumn} not supported`)
      }
    })

  const connectedMember = accountsStore.connectedAccount

  return !isMobile ? (
    // Desktop device
    <Table
      stickyHeader
      aria-label="simple table"
      sx={{
        '& .MuiTableCell-root': {
          px: 1.25,
          py: 0.75,
        },
        '& .MuiTableCell-head': {
          fontSize: '0.82rem',
          fontWeight: 800,
          color: '#687188',
          whiteSpace: 'nowrap',
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'firstName'}
              direction={sortDirection}
              onClick={() => {
                handleSort('firstName', 'asc')
              }}
            >
              {getReadableAccountFieldLabel('firstName')}
            </TableSortLabel>
          </TableCell>
          {/*           <TableCell>
            <TableSortLabel
              active={sortColumn === 'lastName'}
              direction={sortDirection}
              onClick={() => {
                handleSort('lastName', 'asc')
              }}
            >
              {getReadableAccountFieldLabel('lastName')}
            </TableSortLabel>
          </TableCell> */}
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'status'}
              direction={sortDirection}
              onClick={() => {
                handleSort('status', 'asc')
              }}
            >
              Disponibilité
            </TableSortLabel>
          </TableCell>
          <TableCell>Possibilité isolement</TableCell>
          <TableCell>Possibilité soin</TableCell>
          <TableCell>Animaux hors asso</TableCell>
          <TableCell>Surface domicile</TableCell>
          {/* <TableCell>Capacité hébergement</TableCell> */}
          <TableCell>Animaux à charge</TableCell>
          {/* <TableCell>
            <TableSortLabel
              active={sortColumn === 'roles'}
              direction={sortDirection}
              onClick={() => {
                handleSort('roles', 'asc')
              }}
            >
              {getReadableAccountFieldLabel('roles')}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'permission'}
              direction={sortDirection}
              onClick={() => {
                handleSort('permission', 'asc')
              }}
            >
              {getReadableAccountFieldLabel('permission')}
            </TableSortLabel>
          </TableCell> */}
        </TableRow>
      </TableHead>
      <TableBody>
        {connectedMember && searchResult.some((member) => member.id === connectedMember.id) && (
          <MemberListItem member={connectedMember} bgColor={setOpacity(colors.bermudaGray, 0.35)} />
        )}

        {searchResult
          .filter((member) => member.id != connectedMember?.id)
          .map((member) => (
            <MemberListItem key={member.id} member={member} />
          ))}
      </TableBody>
    </Table>
  ) : (
    // Mobile device
    <Stack spacing={1.5} marginTop="10px" marginBottom="80px" marginX="15px">
      {connectedMember && searchResult.some((member) => member.id === connectedMember.id) && (
        <MemberListCard
          member={connectedMember}
          bgColor={setOpacity(colors.bermudaGray, 0.35)}
          index={0}
          expandCardIndex={expandCardIndex}
          setExpandCardIndex={setExpandCardIndex}
        />
      )}

      {searchResult
        .filter((member) => member.id != connectedMember?.id)
        .map((member, index) => (
          <MemberListCard
            key={member.id}
            member={member}
            index={index + 1}
            expandCardIndex={expandCardIndex}
            setExpandCardIndex={setExpandCardIndex}
          />
        ))}
    </Stack>
  )
}

type ItemProps = {
  member: AccountDto
  bgColor?: string
}

const MemberListItem: React.FC<ItemProps> = ({ member, bgColor }) => {
  const navigate = useNavigate()
  const [animals, setAnimals] = React.useState<AnimalSearchItemDto[] | null>(null)
  const [howManyAnimals, setHowManyAnimals] = React.useState<number>(0)

  const handleClick = () => {
    navigate(`${member.id}`)
  }

  const fetchAnimals = async () => {
    if (animals) return
    const fetchedAnimals = await AnimalsClient.getAllAnimalForHostFamilyAccountId(member.id)
    const filteredAnimals = fetchedAnimals.filter(
      (animal) =>
        animal.responsibilities?.hostFamilyInChargeAccountId === member.id &&
        animal.adoptionStatus !== AnimalAdoptionDtoStatus.HasBeenAdopted &&
        !(
          animal.adoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted &&
          (animal.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.Dead ||
            animal.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.Released ||
            animal.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation ||
            animal.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.Lost)
        )
    )
    setAnimals(filteredAnimals)
    setHowManyAnimals(filteredAnimals.length)
  }

  const profileImageKey = member.images?.profileImageKey

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 240,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }))

  return (
    // <TableRow>
    <TableRow
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: bgColor,
        '&:hover': {
          backgroundColor: 'rgba(23, 31, 52, 0.05)',
        },
      }}
    >
      <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
        <Avatar alt={member.firstName} src={profileImageKey && getAccountProfilePhotoSrc(profileImageKey)} />
      </TableCell>
      <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {member.firstName} {member.lastName}
          {member.validated && <Verified sx={{ color: theme.palette.info.main, fontSize: 20 }} />}
        </Box>
        <span style={{ fontWeight: 400, fontStyle: 'italic' }}>
          {member.roles.map((role) => getSmallReadableAccountRole(role)).join(', ')}
        </span>
        <br />
        <span style={{ fontWeight: 400, color: blueSky }}>{getReadableAccountPermission(member.permission)}</span>
      </TableCell>
      <TableCell component="th" scope="row">
        <Box display="flex" justifyContent="center" alignItems="center">
          <Avatar
            sx={{
              background:
                member.visibility?.pauseProfile === true
                  ? colors.geyser
                  : setOpacity(getColorForAvailabilityStatus(isUserAvailableToday(member)), 0.7),
              width: '22px',
              height: '22px',
              fontWeight: 'bold',
            }}
          >
            {' '}
          </Avatar>
        </Box>
      </TableCell>
      <TableCell component="th" scope="row">
        {member.home?.hasIsolationRoom === AccountDtoHomeHasIsolationRoom.Yes ? 'Oui' : 'Non'}
      </TableCell>
      <TableCell component="th" scope="row">
        {member.preferences.primaryPreferences?.withMedicalAntecedent ===
        AccountDtoPreferencesWillingToSocializeWildAnimals.Yes
          ? 'Oui'
          : 'Non'}
      </TableCell>
      <TableCell component="th" scope="row">
        {member.home?.pets?.length && member.home.pets.length > 0 ? 'Oui' : 'Non'}
      </TableCell>
      <TableCell component="th" scope="row">
        {`${member.home?.homeSurface ?? 'NC'} ${member.home?.homeSurface ? 'm²' : ''}`}
      </TableCell>
      <TableCell component="th" scope="row" onClick={(e) => e.stopPropagation()}>
        <HtmlTooltip
          onOpen={async () => fetchAnimals()}
          title={
            animals ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Capacité d&#39;accueil: {howManyAnimals}/{member.home?.maxCapacity || 'NC'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {animals
                    .filter((a) => a.adoptionStatus !== 'has-been-adopted')
                    .map((animal) => (
                      <AnimalProfileChip
                        key={animal.id}
                        animal={animal}
                        chipSx={AnimalChipSxProps}
                        avatarSx={AnimalAvatarSxProps}
                      />
                    ))}
                  {animals.length === 0 && <Typography>Aucun en FA</Typography>}
                </Box>
              </Box>
            ) : (
              <CircularProgress />
            )
          }
        >
          <Typography sx={{ fontSize: '0.8rem', color: '#7A8197', fontWeight: 700, whiteSpace: 'nowrap' }}>
            Voir les animaux en FA
          </Typography>
        </HtmlTooltip>
      </TableCell>
      {/* <TableCell component="th" scope="row">
        {member.roles.map((role) => getReadableAccountRole(role)).join(', ')}
      </TableCell>
      <TableCell component="th" scope="row">
        {getReadableAccountPermission(member.permission)}
      </TableCell> */}
    </TableRow>
  )
}

type ChipProps = {
  animal: AnimalSearchItemDto
  chipSx: SxProps
  avatarSx: SxProps
}
const AnimalChipSxProps = {
  fontSize: 16,
  height: 45,
  backgroundColor: 'white',
}
const AnimalAvatarSxProps = {
  height: '35px !important',
  width: '35px !important',
}

export const AnimalProfileChip: React.FC<ChipProps> = ({ animal, chipSx, avatarSx }) => {
  const navigate = useNavigate()
  return (
    <Chip
      key={animal.id}
      clickable
      onClick={() => navigate(`${PATHS.animals.absolute}/${animal.id}`)}
      label={`${animal.name} ${getEmojiSpecies(animal.species)} ${animal.sex ? getEmojiAnimalSex(animal.sex) : ''} `}
      avatar={
        <Avatar
          alt={`Photo de ${animal.name}`}
          src={animal.images?.profileImageKey && getAnimalProfilePhotoSrc(animal.images.profileImageKey)}
          sx={avatarSx}
        />
      }
      variant="outlined"
      sx={chipSx}
    />
  )
}
